import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Grade, StudentApplicationToApprove } from "../students/types";
import { useTranslation } from "react-i18next";
import PageTitle from "../../components/page-title/PageTitle";
import agent from "../../app/api/agent";
import { downloadFile } from "../../app/helpers/downloadFile";
import Loader from "../../components/Loader";
import { Grid, Header, Label, Segment, SemanticCOLORS, Table, Icon } from "semantic-ui-react";
import { prettifyDate, prettifyDateWithoutTime } from "../../app/helpers/dateHelpers";
import { ApplicationStatus } from "../../app/enums/applicationStatus";
import { ApplicationForSchool } from "./types";
import PendingApplications from "../students/components/PendingApplications";
import Pagination from "../../components/pagination/Pagination";
import StudentPendingApplicationModal from "../students/components/StudentPendingApplicationModal";
import {
  ListFilterConfig,
  ListFilterConfigItemMultiSelectOption,
  ListFilterConfigItemType,
} from "../../components/list-filter/models";
import ListFilter from "../../components/list-filter/ListFilter";
import useApplicationStatuses from "../../app/hooks/useApplicationStatuses";
import useToaster from "../../app/hooks/useToaster";
import { Link } from "react-router-dom";

// Add CSS for loading animation
const loadingAnimation = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const COLOR_STATUS_MAP: { [key: string]: ApplicationStatus[] } = {
  purple: [ApplicationStatus.WAITING_FOR_SCHOOL_APPROVAL, ApplicationStatus.WAITING_FOR_COMPANY_APPROVAL],
  red: [ApplicationStatus.COMPANY_REJECTED, ApplicationStatus.ADMIN_REJECTED, ApplicationStatus.STUDENT_CANCELLED],
  green: [ApplicationStatus.COMPANY_APPROVED],
};

const SchoolStudentsApplicationsPage = () => {
  const { t } = useTranslation();
  const { apiError } = useToaster();
  const [grades, setGrades] = useState<Grade[]>([]);
  const [applications, setApplications] = useState<ApplicationForSchool[]>([]);
  const [pendingApplications, setPendingApplications] = useState<StudentApplicationToApprove[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({ pageNumber: 1, totalPages: 0 });
  const { statusOptions } = useApplicationStatuses();

  const filterConfig: ListFilterConfig = useMemo(
    () => [
      {
        name: "grade",
        title: "students_filter.grade.title",
        type: ListFilterConfigItemType.MultiSelect,
        options: grades.map((g) => ({ key: g.grade, value: g.grade, text: g.grade })),
      },
      {
        name: "status",
        title: "common.status",
        type: ListFilterConfigItemType.MultiSelect,
        options: statusOptions,
      },
      {
        name: "name",
        title: "students_filter.fullName",
        type: ListFilterConfigItemType.Search,
      },
      {
        name: "date",
        title: "common.date",
        type: ListFilterConfigItemType.DateRange,
      },
      {
        name: "hasFilledReflection",
        title: "students_filter.reflection.title",
        type: ListFilterConfigItemType.MultiSelect,
        options: [
          { key: "true", value: "true", text: t("students_filter.reflection.yes") },
          { key: "false", value: "false", text: t("students_filter.reflection.no") },
        ],
      },
      {
        name: "isInternship",
        title: "students_filter.type.title",
        type: ListFilterConfigItemType.MultiSelect,
        options: [
          { key: "false", value: "false", text: t("students_filter.type.shadowing") },
          { key: "true", value: "true", text: t("students_filter.type.internship") },
        ],
      },
    ],
    [grades, statusOptions]
  );

  const [filter, setFilter] = useState<{
    status: ListFilterConfigItemMultiSelectOption[];
    grade: ListFilterConfigItemMultiSelectOption[];
    name: string;
    date: { start: Date; end: Date } | null;
    hasFilledReflection: ListFilterConfigItemMultiSelectOption[];
    isInternship: ListFilterConfigItemMultiSelectOption[];
  }>({ status: [], grade: [], name: "", date: null, hasFilledReflection: [], isInternship: [] });

  const [sorting, setSorting] = useState<{
    column: string | null;
    direction: "ascending" | "descending";
  }>({ column: null, direction: "ascending" });

  useEffect(() => {
    loadGrades();
    loadPendingApplications();
  }, []);

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.pageNumber, sorting]);

  const loadGrades = () => {
    agent.Schools.listGrades().then((response) => setGrades(response));
  };

  const loadPendingApplications = () => {
    agent.Students.listPendingApplications().then((response) => {
      setPendingApplications(response);
    });
  };

  const loadApplications = async () => {
    setLoading(true);

    const isInternshipValues = filter.isInternship.map((v) => v.value);
    const isInternship = isInternshipValues.length === 1 ? isInternshipValues[0] === "true" : undefined;

    const params = {
      status: filter.status.map((v) => String(v.value)),
      grade: filter.grade.map((v) => String(v.value)),
      name: filter.name.trim().length > 0 ? filter.name : undefined,
      activePage: pagination.pageNumber,
      dateFrom: filter.date?.start ? filter.date.start.toISOString().split("T")[0] : undefined,
      dateTo: filter.date?.end ? filter.date.end.toISOString().split("T")[0] : undefined,
      sortBy: sorting.column || undefined,
      sortDirection: sorting.column ? (sorting.direction === "ascending" ? "asc" : "desc") : undefined,
      hasFilledReflection: filter.hasFilledReflection.map((v) => String(v.value)),
      isInternship,
    };

    agent.Applications.listAllForSchool(params)
      .then((response) => {
        setApplications(response.items);
        setPagination({ pageNumber: response.pageNumber, totalPages: response.totalPages });
      })
      .finally(() => setLoading(false));
  };

  const handlePageChange = (pageNumber: number) => {
    setPagination({ ...pagination, pageNumber });
  };

  const handleSort = (column: string) => {
    setSorting((prev) => {
      if (prev.column === column) {
        if (prev.direction === "ascending") {
          // Second click: change to descending
          return { column, direction: "descending" };
        } else {
          // Third click: remove sorting
          return { column: null, direction: "ascending" };
        }
      } else {
        // First click on new column: start with ascending
        return { column, direction: "ascending" };
      }
    });
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const handleApproveApplication = (studentId: number, applicationId: number) => {
    agent.Students.approveApplication(studentId, applicationId)
      .then(() => loadGrades())
      .catch(apiError)
      // Reload either way: after "Application status changed" the list shows the current status.
      .finally(() => {
        loadPendingApplications();
        loadApplications();
      });
  };

  const handleApplicationReturned = () => {
    loadPendingApplications();
    loadApplications();
  };

  const handleApplicationCancellation = (applicationId: number) => {
    agent.Applications.cancel(applicationId).then(() => {
      loadPendingApplications();
      loadApplications();
    });
  };

  const handleFilterChange = (value: {
    grade: ListFilterConfigItemMultiSelectOption[];
    name: string;
    status: ListFilterConfigItemMultiSelectOption[];
    date: { start: Date; end: Date } | null;
    hasFilledReflection: ListFilterConfigItemMultiSelectOption[];
    isInternship: ListFilterConfigItemMultiSelectOption[];
  }) => {
    setFilter(value);
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);

    try {
      const isInternshipValues = filter.isInternship.map((v) => v.value);
      const isInternship = isInternshipValues.length === 1 ? isInternshipValues[0] === "true" : undefined;

      const params = {
        status: filter.status.map((v) => String(v.value)),
        grade: filter.grade.map((v) => String(v.value)),
        name: filter.name.trim() || undefined,
        dateFrom: filter.date?.start?.toISOString().split("T")[0],
        dateTo: filter.date?.end?.toISOString().split("T")[0],
        sortBy: sorting.column || undefined,
        sortDirection: sorting.column ? (sorting.direction === "ascending" ? "asc" : "desc") : undefined,
        hasFilledReflection: filter.hasFilledReflection.map((v) => String(v.value)),
        isInternship,
      };

      const response = await agent.Applications.exportToExcel(params);
      const filename = `aplikacijos-${prettifyDate(new Date().toString())}.xlsx`;

      downloadFile(response.data, filename);
    } catch (error) {
      apiError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const getLabelColor = (status: ApplicationStatus) =>
    (Object.keys(COLOR_STATUS_MAP).find((key) => COLOR_STATUS_MAP[key].includes(status)) || "red") as SemanticCOLORS;

  const getSortIcon = (column: string) => {
    if (sorting.column !== column) return "sort";
    return sorting.direction === "ascending" ? "sort up" : "sort down";
  };

  const renderExportButton = () => (
    <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
      <Link
        to="#"
        onClick={(e) => {
          e.preventDefault();
          handleExportToExcel();
        }}
        style={{
          cursor: isExporting ? "wait" : "pointer",
          pointerEvents: isExporting ? "none" : "auto",
        }}
      >
        {isExporting ? (
          <div style={{ animation: "pulse 1.2s infinite" }}>
            <Icon name="file excel outline" style={{ marginRight: "0.5rem" }} />
            <span>{t("common.loading")}</span>
          </div>
        ) : (
          <>
            <Icon name="file excel outline" style={{ marginRight: "0.5rem" }} />
            {t("common.exportToExcel")}
          </>
        )}
      </Link>
    </div>
  );

  const renderPendingApplications = () => {
    return (
      <>
        {pendingApplications.length > 0 && (
          <PendingApplications
            pendingApplications={pendingApplications}
            handleApproveApplication={handleApproveApplication}
            handleCancelApplication={handleApplicationCancellation}
            handleReturnedApplication={handleApplicationReturned}
          />
        )}
      </>
    );
  };

  const renderApplications = () => {
    if (isLoading) return <Loader />;

    if (applications.length > 0) {
      return (
        <Segment>
          <Table basic="very" unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell
                  sorted={sorting.column === "name" ? sorting.direction : undefined}
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer", border: "none", borderBottom: "1px solid rgba(34,36,38,.15)" }}
                >
                  <Icon name={getSortIcon("name") as any} />
                  {t("common.student")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={sorting.column === "position" ? sorting.direction : undefined}
                  onClick={() => handleSort("position")}
                  style={{ cursor: "pointer", border: "none", borderBottom: "1px solid rgba(34,36,38,.15)" }}
                >
                  <Icon name={getSortIcon("position") as any} />
                  {t("common.position")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={sorting.column === "date" ? sorting.direction : undefined}
                  onClick={() => handleSort("date")}
                  style={{ cursor: "pointer", border: "none", borderBottom: "1px solid rgba(34,36,38,.15)" }}
                >
                  <Icon name={getSortIcon("date") as any} />
                  {t("common.date")}
                </Table.HeaderCell>
                <Table.HeaderCell style={{ borderBottom: "1px solid rgba(34,36,38,.15)" }}></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {applications.map((application) => (
                <Table.Row key={application.id}>
                  <Table.Cell>
                    <Header>
                      <Header.Content style={{ fontSize: "16px" }}>
                        {application.student.fullName},{" "}
                        {application.student.isStudent ? "Studentas" : `${application.student.grade} kl.`}
                        <Header.Subheader>
                          <a href={"mailto:" + application.student.email}>{application.student.email}</a>
                        </Header.Subheader>
                      </Header.Content>
                    </Header>
                  </Table.Cell>
                  <Table.Cell>
                    <Header>
                      <Header.Content style={{ fontSize: "14px" }}>
                        {application.shadowing.position}
                        {application.shadowing.isInternship && (
                          <Label color="teal" size="tiny" style={{ marginLeft: "0.5em", verticalAlign: "middle" }}>
                            {t("common.internship")}
                          </Label>
                        )}
                        <Header.Subheader>{application.shadowing.companyName}</Header.Subheader>
                        <Header.Subheader>
                          {application.shadowing.online ? (
                            t("shadowing.remote")
                          ) : (
                            <>
                              {application.shadowing.address}, {application.shadowing.city}
                            </>
                          )}
                        </Header.Subheader>
                      </Header.Content>
                    </Header>
                  </Table.Cell>
                  <Table.Cell>
                    <Header>
                      <Header.Content style={{ fontSize: "14px" }}>
                        {application.shadowing.isInternship
                          ? `${prettifyDateWithoutTime(application.shadowing.date)}${application.shadowing.endDate ? ` – ${prettifyDateWithoutTime(application.shadowing.endDate)}` : ""}`
                          : prettifyDate(application.shadowing.date)}
                        <Header.Subheader>
                          <Label color={getLabelColor(application.status)} size="small" style={{ marginLeft: "0" }}>
                            {t(`applications.status.${application.status}`)}
                          </Label>
                          {application.noShow && (
                            <Label color="black" size="small" style={{ marginLeft: "0.3em" }}>
                              {t("applications.student.no_show")}
                            </Label>
                          )}
                        </Header.Subheader>
                      </Header.Content>
                    </Header>
                  </Table.Cell>
                  <Table.Cell className="student-list-actions" singleLine>
                    <StudentPendingApplicationModal
                      studentId={application.student.id}
                      applicationId={application.id}
                      studentFullName={application.student.fullName}
                      grade={application.student.isStudent ? "Studentas" : application.student.grade}
                      handleCancel={handleApplicationCancellation}
                      handleReturn={handleApplicationReturned}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      );
    }

    return <>{t("common.no_records")}</>;
  };

  return (
    <Fragment>
      <style>{loadingAnimation}</style>
      <PageTitle title={t("common.studentsApplications")} />
      {renderPendingApplications()}
      <Grid>
        <ListFilter config={filterConfig} value={filter as any} onChange={(v) => handleFilterChange(v as any)} />
      </Grid>
      {renderExportButton()}
      {renderApplications()}
      <Pagination
        totalPages={pagination.totalPages}
        pageNumber={pagination.pageNumber}
        handlePageChange={handlePageChange}
      />
    </Fragment>
  );
};

export default SchoolStudentsApplicationsPage;
